import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

import { FaqItem } from '../interface/faq.modal';

interface MetaTagsConfig {
  title?: string;
  description?: string;
  canonical?: string;
  robot_noindex?: string;
  langCode?: string;

  ogTitle?: string;
  ogDesc?: string;
  ogImage?: string;
  ogUrl?: string;
  ogImageType?: string;
  ogImageAlt?: string;
  ogLocale?: string;
  twitterTitle?: string;
  twitterDesc?: string;
  twitterImage?: string;
  twitterImageAlt?: string;

  hrefLang?: string;
  hrefLangs?: { lang: string; href: string }[];
  jsonLd?: object;
  ratingSchema?: string;
  organizationSchema?: string;
}

@Injectable({ providedIn: 'root' })
export class MetaDataService {
  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  updateTags(config: MetaTagsConfig) {
    if (config.title) {
      this.title.setTitle(config.title);
    }

    if (config.description) {
      this.meta.updateTag({ name: 'description', content: config.description });
    }

    if (config.robot_noindex) {
      this.meta.updateTag({ name: 'robots', content: config.robot_noindex });
    }

    if (config.langCode) {
      this.meta.updateTag({ name: 'langCode', content: config.langCode });
    }

    this.updateCanonical(config.canonical);
    this.updateOG(config);
    this.updateTwitterCard(config);

    if (config.hrefLang) {
      this.updateHrefLang(config.hrefLang);
    }

    if (config.hrefLangs?.length) {
      this.updateHrefLangs(config.hrefLangs);
    }

    if (config.jsonLd) {
      this.updateJsonLd(config.jsonLd);
    }
  }

  // -----------------------------
  // CANONICAL TAG
  // -----------------------------
  private updateCanonical(url?: string) {
    if (!url) return;

    const link: HTMLLinkElement =
      this.doc.querySelector("link[rel='canonical']") || this.doc.createElement('link');

    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', 'https://infraon.io' + url);

    if (!link.parentNode) {
      this.doc.head.appendChild(link);
    }
  }

  // -----------------------------
  // OPEN GRAPH TAGS
  // -----------------------------
  private updateOG(config: MetaTagsConfig) {
    const currentUrl = this.doc?.defaultView?.location?.href || config.ogUrl || '';

    const ogTags = [
      { property: 'og:title', content: config.ogTitle || config.title || '' },
      { property: 'og:description', content: config.ogDesc || config.description || '' },
      { property: 'og:image', content: config.ogImage || '' },
      { property: 'og:url', content: config.ogUrl || currentUrl },
      { property: 'og:image:type', content: config.ogImageType || '' },
      { property: 'og:image:alt', content: config.ogImageAlt || '' },
      { property: 'og:locale', content: config.ogLocale || '' },
    ];

    ogTags.forEach((tag) => {
      if (tag.content) {
        this.meta.updateTag(tag);
      }
    });
  }

  //------------------------------
  // TWITTER CARDS
  //
  private updateTwitterCard(config: MetaTagsConfig) {
    const twitterTags = [
      { name: 'twitter:title', content: config.twitterTitle || config.title || '' },
      { name: 'twitter:description', content: config.twitterDesc || config.description || '' },
      { name: 'twitter:image', content: config.twitterImage || '' },
      { name: 'twitter:image:alt', content: config.twitterImageAlt || '' },
    ];

    twitterTags.forEach((tag) => {
      if (tag.content) {
        this.meta.updateTag(tag);
      }
    });
  }

  // -----------------------------
  // HREFLANG
  // -----------------------------
  private updateHrefLang(lang: string) {
    const href = this.doc?.defaultView?.location?.href || '';

    const link: HTMLLinkElement =
      this.doc.querySelector("link[rel='alternate'][hreflang]") || this.doc.createElement('link');

    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', href);

    if (!link.parentNode) {
      this.doc.head.appendChild(link);
    }
  }

  // -----------------------------
  // HREFLANG (multiple alternates, e.g. per-country/locale pages)
  // -----------------------------
  private updateHrefLangs(links: { lang: string; href: string }[]) {
    this.doc.querySelectorAll("link[rel='alternate'][hreflang]").forEach((el) => el.remove());

    links.forEach(({ lang, href }) => {
      const link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      this.doc.head.appendChild(link);
    });
  }

  // -----------------------------
  // JSON-LD (SSR ENABLED)
  // -----------------------------
  private updateJsonLd(json: object) {
    let script = this.doc.getElementById('jsonld-script') as HTMLScriptElement;

    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'jsonld-script';
      this.doc.head.appendChild(script);
    }

    script.textContent = typeof json === 'string' ? json : JSON.stringify(json);
  }

  updateBreadcrumbJsonLd(breadcrumbs: { label: string; url: string }[]) {
    if (!breadcrumbs?.length) return;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: b.label,
        item: b.url,
      })),
    };

    let script = this.doc.getElementById('breadcrumb-jsonld') as HTMLScriptElement;

    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'breadcrumb-jsonld';
      this.doc.head.appendChild(script);
    }

    script.textContent = JSON.stringify(jsonLd);
  }

  // -----------------------------
  // FAQ JSON-LD  ✅ (NEW)
  // -----------------------------
  updateFaqJsonLd(faqs: FaqItem[]) {
    if (!faqs?.length) return;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => {
        const paragraphText = faq.answer?.paragraphs?.join(' ') ?? '';

        const listText = faq.answer?.list?.items?.join(' ') ?? '';

        const finalText = [paragraphText, listText].filter(Boolean).join(' ');

        return {
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: finalText,
          },
        };
      }),
    };

    let script = this.doc.getElementById('faq-jsonld') as HTMLScriptElement;

    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'faq-jsonld';
      this.doc.head.appendChild(script);
    }

    script.textContent = JSON.stringify(jsonLd);
  }

  // -----------------------------
  // Arbitrary JSON-LD graph, keyed by a caller-supplied script id
  // (e.g. a page-specific Organization/WebPage/SoftwareApplication graph)
  // -----------------------------
  injectJsonLdById(id: string, json: object) {
    let script = this.doc.getElementById(id) as HTMLScriptElement;

    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.doc.head.appendChild(script);
    }

    script.textContent = JSON.stringify(json);
  }
}
